import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { capitalize } from 'utils';
import { AppleID } from '../apple-id';
import { GoogleMap } from '../google-map';
import { SessionInformationLoader } from 'views/sessions/session-details-loader';
import { NoData } from '../no-data-available';
import {
	ComplexSelectedSession,
	ComplexSessionInfoState,
	ConditionalSessionFlowState,
	SelectedSessionState,
	loginState,
} from 'global-stores';
import { FacialInformationSection } from '../facial-section';
import { LinkedEsignSession } from '../linked-esign-session';
import { REACT_APP_FENNEMORE_BUSINESSID } from 'envs';
// import { AcceptedAgreements } from 'views';

interface ISessionInfo {
	loaded?: boolean;
	isLoading?: boolean;
	sessionData?: any;
}

const SessionInfo = ({ loaded, isLoading, sessionData }: ISessionInfo) => {
	const user = useRecoilValue(loginState);
	const selectedSession = useRecoilValue(SelectedSessionState);
	const selectedComplexSession = useRecoilValue(ComplexSelectedSession);
	const isComplexFlow = useRecoilValue(ConditionalSessionFlowState);
	const complexSessionInfo = useRecoilValue(ComplexSessionInfoState);
	const { deviceInfo, sessionMetadata, geoLocation } = useMemo(() => {
		if (isComplexFlow) {
			return (sessionData ?? complexSessionInfo)?.metadata ?? ({} as any);
		}
		return (
			selectedSession?.device ?? selectedSession?.deviceInfo ?? ({} as any)
		);
	}, [
		isComplexFlow,
		selectedSession?.device,
		selectedSession?.deviceInfo,
		sessionData,
		complexSessionInfo,
	]);
	const isDeviceInfoAvailable = useMemo(() => {
		return !!(Object.keys(deviceInfo ?? {})?.length < 0);
	}, [deviceInfo]);

	const { ipAddress, systemData, userAddress } = sessionMetadata ?? {};

	const { browser, device, os } = systemData ?? {};

	const { city, country_code, region } = userAddress ?? {};

	const geoLocationAvailable = useMemo(() => {
		const { latitude } = geoLocation ?? {};
		return !!latitude;
	}, [geoLocation]);

	const isInfoAvailable = useMemo(() => {
		const { name } = deviceInfo ?? {};
		return !!name;
	}, [deviceInfo]);

	return (
		<div className="session-info">
			<div className="kycDetails-header">
				<div className="kycDetails--sub-header">
					<span className="form-icon-container">
						<span className="form-icon-profile-container">
							<i className="ri-device-line form-icon-profile-container__formIcon" />
						</span>
						<span>Session Information</span>
					</span>
				</div>
				<span className="session-info__device">
					<i
						className={
							(device?.type ?? deviceInfo?.type) === 'mobile'
								? 'ri-smartphone-line'
								: 'ri-macbook-line'
						}
					/>
					{device?.type || deviceInfo?.type
						? capitalize(device?.type ?? deviceInfo?.type)
						: 'Desktop'}
				</span>
			</div>
			{/* Design was not confirm so made last moment changes and commented some code for confirmation  */}
			{!isLoading && loaded ? (
				<>
					{!!sessionMetadata ||
					geoLocationAvailable ||
					isInfoAvailable ||
					!isDeviceInfoAvailable ? (
						<>
							<div className="session-info__body">
								<div className="session-info__body__details-container">
									<div className="session-info__body__details">
										<div className="session-info__body__icon-container device">
											<i className="ri-smartphone-line" />
										</div>
										<div className="session-info__body__detailed">
											<span className="session-info__body__key">Device OS</span>
											<span className="session-info__body__value">
												{os?.name && os?.version
													? `${os?.name} ${os?.version}`
													: 'NA'}
											</span>
										</div>
									</div>

									<div className="session-info__body__details">
										<div className="session-info__body__icon-container user">
											<i className="ri-user-search-line" />
										</div>
										<div className="session-info__body__detailed">
											<span className="session-info__body__key">User IP</span>
											<span className="session-info__body__value">
												{ipAddress || 'NA'}
											</span>
										</div>
									</div>
									<div className="session-info__body__details">
										<div className="session-info__body__icon-container host">
											<i className="ri-map-pin-2-line" />
										</div>
										<div className="session-info__body__detailed">
											<span className="session-info__body__key">Region</span>
											<span className="session-info__body__value">
												{(region ?? 'NA').toUpperCase()}
											</span>
										</div>
									</div>
								</div>
								<div className="session-info__body__details-container">
									<div className="session-info__body__details">
										<div className="session-info__body__icon-container browser">
											<i className="ri-global-line" />
										</div>
										<div className="session-info__body__detailed">
											<span className="session-info__body__key">Browser</span>
											<span className="session-info__body__value">
												{browser?.name && browser?.major
													? `${browser?.name} ${browser?.major}`
													: 'NA'}
											</span>
										</div>
									</div>

									<div className="session-info__body__details">
										<div className="session-info__body__icon-container location">
											<i className="ri-user-location-line" />
										</div>
										<div className="session-info__body__detailed">
											<span className="session-info__body__key">City</span>
											<span className="session-info__body__value">
												{city ? capitalize(city) : 'NA'}
											</span>
										</div>
									</div>
									<div className="session-info__body__details">
										<div className="session-info__body__icon-container referrer">
											<i className="ri-flag-line" />
										</div>
										<div className="session-info__body__detailed">
											<span className="session-info__body__key">
												Country Code
											</span>
											<span
												className="session-info__body__value"
												style={{ textTransform: 'uppercase' }}
											>
												{country_code ?? 'NA'}
											</span>
										</div>
									</div>
								</div>
							</div>
							<AppleID sessionData={sessionData} />

							<GoogleMap sessionData={sessionData} />
							{REACT_APP_FENNEMORE_BUSINESSID !== user?.business && selectedComplexSession?._id && <LinkedEsignSession sessionId={selectedComplexSession?._id}/>}
							{/* <AcceptedAgreements/> */}
							<FacialInformationSection sessionData={sessionData}/>
						</>
					) : (
						<NoData />
					)}
				</>
			) : (
				<SessionInformationLoader />
			)}
		</div>
	);
};

export { SessionInfo };
